/**
 * Preparing a recording for upload: hash it, decode it, measure it.
 *
 * All of this happens in the browser before a single byte leaves the device,
 * which is what lets the upload start while the contributor is still answering
 * the A–B–C questions. That parallelism is the main reason a contribution can
 * finish in under a minute.
 */

import { UserFacingError } from '../user-facing-error.js';

export interface PreparedAudio {
  file: File;
  /** Lowercase hex, as the API requires. Storage verifies this itself. */
  checksumSha256: string;
  durationMs: number;
  sampleRateHz: number;
  channels: number;
  /** Decoded mono samples, for the spectrogram. Not uploaded. */
  peaks: Float32Array;
  lossless: boolean;
}

const LOSSLESS_TYPES = new Set(['audio/flac', 'audio/x-flac', 'audio/wav', 'audio/x-wav']);

export const ACCEPTED_TYPES = [
  'audio/flac',
  'audio/x-flac',
  'audio/wav',
  'audio/x-wav',
  'audio/mpeg',
  'audio/mp4',
  'audio/aac',
  'audio/ogg',
  'audio/webm',
];

export class AudioPrepareError extends UserFacingError {}

/**
 * Hashes and decodes a file.
 *
 * The checksum is computed over the exact bytes that will be uploaded — not
 * over the decoded audio — because it is verified against the stored object.
 */
export async function prepareAudio(file: File): Promise<PreparedAudio> {
  if (file.size === 0) {
    throw new AudioPrepareError('That file is empty.');
  }

  const bytes = await file.arrayBuffer();

  // SubtleCrypto requires a secure context. On http:// (other than localhost)
  // it is simply absent, and the failure is otherwise a confusing TypeError.
  if (!globalThis.crypto?.subtle) {
    throw new AudioPrepareError(
      'This browser cannot hash files on an insecure connection. Use HTTPS.',
    );
  }

  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const checksumSha256 = [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  // decodeAudioData consumes (detaches) its buffer, so decode from a copy —
  // otherwise the File's bytes become unusable for the upload that follows.
  const AudioCtx = window.AudioContext ?? (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtx) throw new AudioPrepareError('This browser cannot decode audio.');

  const context = new AudioCtx();
  let decoded: AudioBuffer;
  try {
    decoded = await context.decodeAudioData(bytes.slice(0));
  } catch {
    throw new AudioPrepareError(
      'That file could not be read as audio. Try a WAV, FLAC, MP3 or M4A file.',
    );
  } finally {
    void context.close();
  }

  if (decoded.duration < 0.5) {
    throw new AudioPrepareError('That recording is shorter than half a second.');
  }

  return {
    file,
    checksumSha256,
    durationMs: Math.round(decoded.duration * 1000),
    sampleRateHz: decoded.sampleRate,
    channels: decoded.numberOfChannels,
    peaks: toMono(decoded),
    lossless: LOSSLESS_TYPES.has(file.type),
  };
}

/**
 * Mixes to mono for analysis and display.
 *
 * Only the spectrogram uses this. The uploaded file keeps every channel it
 * came with — downmixing what goes into the corpus would discard information
 * that a future analysis might need, and it cannot be recovered.
 */
function toMono(buffer: AudioBuffer): Float32Array {
  if (buffer.numberOfChannels === 1) return buffer.getChannelData(0).slice();

  const length = buffer.length;
  const mono = new Float32Array(length);

  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const data = buffer.getChannelData(channel);
    for (let i = 0; i < length; i++) {
      mono[i] = (mono[i] ?? 0) + (data[i] ?? 0) / buffer.numberOfChannels;
    }
  }

  return mono;
}

export function formatDuration(ms: number): string {
  const total = Math.round(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
